import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { XIcon } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Field, FieldDescription, FieldLabel } from '@/components/ui/field';
import { FormModal } from '@/components/ui/form-modal';
import { Input } from '@/components/ui/input';
import { SearchableSelect } from 'components/IncidentConfig/roster/SearchableSelect';
import {
  useAorReportApprovalPolicy,
  useSaveAorReportApprovalPolicy,
  type AorApprovalAssignee,
} from 'components/Reports/hooks/useAorReportApprovalPolicy';

type AorApprovalSettingsDialogProps = {
  open: boolean;
  aorId: string;
  onOpenChange: (open: boolean) => void;
};

type StepDraft = { assignees: AorApprovalAssignee[] };

const MAX_STEPS = 10;

const assigneeKey = (assignee: AorApprovalAssignee) =>
  assignee.kind === 'user' ? `user:${assignee.userGuid}` : `role:${assignee.roleId}`;

type StepEditorProps = {
  initialSteps: StepDraft[];
  users: { userGuid: string; displayName: string }[];
  roles: { roleId: string; name: string }[];
  onSave: (steps: StepDraft[]) => void;
};

const StepEditor: React.FC<StepEditorProps> = ({ initialSteps, users, roles, onSave }) => {
  const [steps, setSteps] = useState(initialSteps);

  const setStepCount = (count: number) => {
    const nextCount = Math.min(MAX_STEPS, Math.max(0, count));
    setSteps((current) => {
      if (nextCount === current.length) return current;
      if (nextCount < current.length) return current.slice(0, nextCount);
      return [
        ...current,
        ...Array.from({ length: nextCount - current.length }, () => ({ assignees: [] })),
      ];
    });
  };

  const addAssignee = (stepIndex: number, assignee: AorApprovalAssignee) => {
    setSteps((current) =>
      current.map((step, index) => {
        if (index !== stepIndex) return step;
        if (step.assignees.some((existing) => assigneeKey(existing) === assigneeKey(assignee))) {
          return step;
        }
        return { assignees: [...step.assignees, assignee] };
      })
    );
  };

  const removeAssignee = (stepIndex: number, key: string) => {
    setSteps((current) =>
      current.map((step, index) =>
        index === stepIndex
          ? { assignees: step.assignees.filter((assignee) => assigneeKey(assignee) !== key) }
          : step
      )
    );
  };

  return (
    <form
      id="aor-approval-settings"
      className="tw:flex tw:flex-col tw:gap-4"
      onSubmit={(event) => {
        event.preventDefault();
        onSave(steps);
      }}
    >
      <Field>
        <FieldLabel htmlFor="aor-approval-step-count">Number of approval steps</FieldLabel>
        <FieldDescription>
          Steps run in order. Anyone named on the current step, or anyone who holds a named role, can approve it.
          Set this to 0 to use the default reviewer list again.
        </FieldDescription>
        <Input
          id="aor-approval-step-count"
          type="number"
          min={0}
          max={MAX_STEPS}
          value={steps.length}
          onChange={(event) => setStepCount(Number(event.target.value))}
        />
      </Field>
      {steps.map((step, index) => (
        <Field key={index}>
          <FieldLabel>Step {index + 1}</FieldLabel>
          <div className="tw:flex tw:flex-wrap tw:gap-1.5">
            {step.assignees.map((assignee) => (
              <Badge key={assigneeKey(assignee)} variant="secondary" className="tw:h-6 tw:rounded-full tw:pr-1">
                {assignee.kind === 'role' ? `Role: ${assignee.displayName}` : assignee.displayName}
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-xs"
                  aria-label={`Remove ${assignee.displayName}`}
                  onClick={() => removeAssignee(index, assigneeKey(assignee))}
                >
                  <XIcon />
                </Button>
              </Badge>
            ))}
          </div>
          <SearchableSelect
            aria-label={`Add a user to step ${index + 1}`}
            placeholder="Add a user…"
            options={users.map((user) => ({ value: user.userGuid, label: user.displayName }))}
            value={null}
            onChange={(next) => {
              if (!next) return;
              addAssignee(index, { kind: 'user', userGuid: next.value, displayName: next.label });
            }}
            positionerClassName="tw:z-[70]"
          />
          <SearchableSelect
            aria-label={`Add a role to step ${index + 1}`}
            placeholder="Add a role…"
            options={roles.map((role) => ({ value: role.roleId, label: role.name }))}
            value={null}
            onChange={(next) => {
              if (!next) return;
              addAssignee(index, { kind: 'role', roleId: next.value, displayName: next.label });
            }}
            positionerClassName="tw:z-[70]"
          />
        </Field>
      ))}
    </form>
  );
};

const AorApprovalSettingsDialog: React.FC<AorApprovalSettingsDialogProps> = ({
  open,
  aorId,
  onOpenChange,
}) => {
  const policyQuery = useAorReportApprovalPolicy(open ? aorId : null);
  const savePolicy = useSaveAorReportApprovalPolicy(aorId);

  const save = async (steps: StepDraft[]) => {
    const filledSteps = steps.filter((step) => step.assignees.length > 0);
    try {
      await savePolicy.mutateAsync(
        filledSteps.map((step) => ({
          assignees: step.assignees.map((assignee) =>
            assignee.kind === 'user'
              ? { kind: 'user' as const, userGuid: assignee.userGuid }
              : { kind: 'role' as const, roleId: assignee.roleId }
          ),
        }))
      );
      toast.success(
        filledSteps.length === 0
          ? 'Approval settings cleared for this area'
          : 'Approval settings saved for this area'
      );
      onOpenChange(false);
    } catch {
      toast.error('Failed to save approval settings');
    }
  };

  const loadedSteps =
    policyQuery.data?.steps.map((step) => ({ assignees: [...step.assignees] })) ?? [];

  return (
    <FormModal
      open={open}
      onOpenChange={onOpenChange}
      title="Approval settings"
      description="These steps apply to every report that describes this area. They replace the default reviewer list."
      widthClassName="tw:!max-w-xl tw:sm:!max-w-xl"
      footer={
        <>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={savePolicy.isPending}>
            Cancel
          </Button>
          <Button type="submit" form="aor-approval-settings" disabled={savePolicy.isPending || !policyQuery.data}>
            Save
          </Button>
        </>
      }
    >
      {!policyQuery.data ? (
        <p className="tw:m-0 tw:text-sm tw:text-muted-foreground">Loading approval settings…</p>
      ) : (
        <StepEditor
          key={`${aorId}:${policyQuery.dataUpdatedAt}`}
          initialSteps={loadedSteps}
          users={policyQuery.data.users}
          roles={policyQuery.data.roles}
          onSave={(steps) => void save(steps)}
        />
      )}
    </FormModal>
  );
};

export default AorApprovalSettingsDialog;
